import type { SummaryStat } from '../data/types'
import {
  CheckCircleIcon,
  ClipboardIcon,
  DocumentIcon,
  MapIcon,
  // ShieldIcon,
  TrendDownIcon,
  TrendUpIcon,
  UsersIcon,
  XCircleIcon,
} from './icons'

type StatCardsProps = {
  stats: SummaryStat[]
}

const statIcons: Record<string, typeof UsersIcon> = {
  'total-docs': DocumentIcon,
  'pending-docs': ClipboardIcon,
  'total-citizens': UsersIcon,
  'pending-applications': ClipboardIcon,
  'approved-today': CheckCircleIcon,
  'rejected-today': XCircleIcon,
  'active-officers': UsersIcon,
  'total-regions': MapIcon,
};

const statColors: Record<string, string> = {
  'total-docs': 'bg-blue-50 text-blue-700',
  'pending-docs': 'bg-amber-50 text-amber-700',
  'total-citizens': 'bg-blue-50 text-blue-700',
  'pending-applications': 'bg-amber-50 text-amber-700',
  'approved-today': 'bg-emerald-50 text-emerald-700',
  'rejected-today': 'bg-red-50 text-red-700',
  'active-officers': 'bg-indigo-50 text-indigo-700',
  'total-regions': 'bg-sky-50 text-sky-700',
};

function formatNumber(value: number): string {
  return value.toLocaleString('en-US')
}

export function StatCards({ stats }: StatCardsProps) {
  return (
    <section aria-label="Summary statistics">
  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-6">
    {stats.map((stat) => {
      const Icon = statIcons[stat.id] ?? DocumentIcon
      const colorClass = statColors[stat.id] ?? 'bg-blue-50 text-blue-700'
      const trend = stat.trend
      const isUp = trend?.direction === 'up'
      const trendColorClass = trend?.impact === 'positive'
        ? 'text-emerald-600'
        : trend?.impact === 'negative'
          ? 'text-red-600'
          : 'text-slate-500'

      return (
        <article
          key={stat.id}
          className="flex flex-col rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
        >
          {/* Row 1: Icon */}
          <div className="flex items-start justify-between gap-3">
            <div className={`rounded-lg p-2 ${colorClass}`}>
              <Icon className="h-5 w-5" />
            </div>
          </div>
          
          {/* Row 2: Label (First line aligned) */}
          <div className="mt-3 min-h-[40px] flex items-start">
            <p className="text-sm font-medium text-slate-500 leading-tight">
              {stat.label}
            </p>
          </div>

          <p className="mt-1 text-xs text-slate-400">
            {stat.period ? `Period: ${stat.period}` : 'Reporting period not provided'}
          </p>
          
          {/* Row 3: Value (Perfectly level) */}
          <p className="mt-1 text-2xl font-bold tracking-tight text-slate-900">
            {formatNumber(stat.value)}
          </p>
          
          {/* Row 4: Trend (First line aligned & locked to bottom of card) */}
          <div className="mt-auto pt-2 min-h-[40px] flex items-start">
            {trend && (
              <div
                className={`flex items-start gap-1 text-xs font-medium leading-tight ${trendColorClass}`}
              >
                {trend.comparisonPeriod && (
                  isUp ? (
                    <TrendUpIcon className="h-3.5 w-3.5 shrink-0" />
                  ) : (
                    <TrendDownIcon className="h-3.5 w-3.5 shrink-0" />
                  )
                )}
                <span>
                  {trend.comparisonPeriod
                    ? `${trend.label} vs ${trend.comparisonPeriod}`
                    : trend.label}
                  {trend.comparisonPeriod && trend.impact
                    ? ` · ${trend.impact.charAt(0).toUpperCase()}${trend.impact.slice(1)} impact`
                    : trend.comparisonPeriod
                      ? ' · Impact not specified'
                      : ''}
                </span>
              </div>
            )}
          </div>
        </article>
      )
    })}
  </div>
</section>
  )
}